import { useMemo } from 'react'

import { faClipboardList } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { sendGTMEvent } from '@b-stock/fe-core/bstock-next/components'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, Tag, TagType } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows, Typography } from '@b-stock/fe-core/design-system'

import FmvDealBadge from '@/buyer-portal/components/features/listing/ListingActionColumn/Bid/FmvDealBadge'
import Image from '@/components/Image'
import { CompareCardOverlay } from '@/components/listings/compare/CompareCardOverlay'
import { useOptionalCompareSelection } from '@/components/listings/compare/CompareSelectionProvider'
import { getWatchlistDateExpires } from '@/helpers/dateUtils'
import type { ManifestSummarizeLabelVariant } from '@/helpers/featureFlags'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { searchResultToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { useItemListContext } from '@/helpers/telemetry/itemListContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { transformListingToGTMItem } from '@/helpers/telemetry/transformListingToGTMItem'
import { useDealBadgeData } from '@/hooks/useDealBadgeData'
import type { SearchResult } from '@/queries/listingSearchQuery'

import {
  useShowsCardNote,
  WatchlistCTA,
  WatchlistNotePopover,
} from '../../watchlist'
import isBstockBlue from './isBstockBlue'

/**
 * Success.background is a 3% wash that reads as white beside an untreated
 * card, so the promoted tint is mixed from the family's saturated green
 * instead. 10% is deliberately quiet — about half the saturation of
 * BStock.backgroundSelected, the tint the B-Stock Blue card already uses — so
 * a promoted card reads as treated beside an untreated one without pulling
 * attention from the blue card next to it. The green border carries most of
 * the signal at this weight.
 */
export const PROMOTED_TINT = `color-mix(in srgb, ${Colors.Semantic.Success.onhover} 10%, ${Colors.Semantic.Neutral.white})`

export const Wrapper = styled.div`
  display: grid;
  position: relative;
`

/*
 * The card's border/background/padding all vary with the same handful of
 * listing states. Expressed as styled-components prop interpolations, every one
 * of these runs a function + re-hash per card per render, and the ALP renders
 * dozens of cards per request — a measurable SSR cost. Instead the component
 * emits `data-*` attributes and all the variant styling lives in static CSS
 * selectors below, which styled-components compiles once. See the
 * data-bstock-blue / data-promoted / data-variant / data-compare-selected /
 * data-has-summarize-button attributes on the usage site.
 */
export const CardLink = styled.a`
  display: flex;
  min-width: 14.25rem;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  /* Default (untreated) card. */
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};

  /* Background is driven independently of the border variant: the original
     background keyed only on promoted/bstock-blue, so a new-seller card that is
     also promoted still gets the promoted tint. B-Stock Blue lots are never
     promoted, so a card carrying both is a broken upstream response; the tint
     wins that case so the promotion still reads, and the blue badge still
     renders (data-promoted is emitted after data-bstock-blue below and this
     rule follows the bstock-blue rule, so it wins on equal specificity). */
  background: ${Colors.Semantic.Neutral.white};

  &[data-bstock-blue='true'] {
    background: ${Colors.Semantic.BStock.backgroundSelected};
  }

  &[data-promoted='true'] {
    background: ${PROMOTED_TINT};
  }

  &[data-variant='new-seller'] {
    border: 0.1225rem solid ${Colors.Semantic.SpecificFunctions.Rating.rating};
    box-shadow: 0 0 0.4rem ${Colors.Semantic.SpecificFunctions.Rating.rating};
  }

  /* The tint is a soft wash, so the edge reinforces it. Same width as the
     default border, so nothing shifts. */
  &[data-variant='promoted'] {
    border: 0.0625rem solid ${Colors.Semantic.Success.copy};
  }

  &[data-has-summarize-button='true'] {
    padding-bottom: 2.5rem;
  }

  &:hover {
    ${Shadows.Cards}
  }

  /* Compare selection wins over the new-seller border and keeps its glow on
     hover (declared after &:hover so it overrides Shadows.Cards). */
  &[data-compare-selected='true'] {
    border: 0.125rem solid ${Colors.Semantic.BStock.default.lightBackground};
    box-shadow: 0 0 0 0.25rem ${Colors.Semantic.BStock.backgroundSelected};

    &:hover {
      box-shadow: 0 0 0 0.25rem ${Colors.Semantic.BStock.backgroundSelected};
    }
  }
`

export const ImageSection = styled.div`
  height: 10.5rem;
  flex-shrink: 0;
  align-self: stretch;
  position: relative;

  img {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    object-fit: contain;
  }
`

const BidStatusTag = styled(Tag)<{ $isCompareMode?: boolean }>`
  position: absolute;
  /* In compare mode the checkbox overlay owns the top-left corner, so push the
     status tag below it: the overlay's own 0.75rem inset plus its 2rem height,
     then the same 0.75rem gap the card uses elsewhere so the tag does not sit
     flush against the checkbox. */
  top: ${({ $isCompareMode }) => ($isCompareMode ? '3.5rem' : '0.75rem')};
  left: 0.75rem;
  ${Shadows.MultipleCards}
  outline: 0.0625rem solid rgba(20, 26, 30, 0.05);
  outline-offset: -0.0625rem;
`

const TopRightContainer = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  flex-direction: row-reverse;
  gap: 0.75rem;
  align-items: flex-start;
`

const CtaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`

const BadgesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`

const SummarizeButtonContainer = styled.div`
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  z-index: 1;
`

const SummarizeButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  ${Typography.Body3}
  background: ${Colors.Semantic.BStock.background.lightBackground};
  color: ${Colors.Semantic.BStock.default.lightBackground};

  &:hover {
    filter: brightness(0.93);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`

const BStockBlueTag = styled(Tag)`
  background-color: ${Colors.Semantic.Info.onhover};
  color: ${Colors.Semantic.Neutral.white};
  border: 1px solid ${Colors.Semantic.Neutral.white};
`

const PromotedTag = styled(Tag)`
  background-color: ${Colors.Semantic.Success.copy};
  color: ${Colors.Semantic.Neutral.white};
  border: 0.0625rem solid ${Colors.Semantic.Neutral.white};
`

const getStatusTag = (listing: SearchResult, bidStatus: BidStatus) => {
  const isMAOListing = listing.pricingStrategy === 'MAKE_AN_OFFER'

  if (isMAOListing) {
    return getMAOStatusTag(listing)
  }

  return getAuctionStatusTag(listing, bidStatus)
}

const getMAOStatusTag = (listing: SearchResult) => {
  if (listing.offering?.listingStatus === 'CANCELED') {
    return {
      type: TagType.error,
      messageId: 'Auction.tag.canceled',
    }
  }

  if (listing.offering?.listingStatus === 'COMPLETED') {
    return {
      type: TagType.normal,
      messageId: 'Auction.tag.ended',
    }
  }

  return null
}

const getAuctionStatusTag = (listing: SearchResult, bidStatus: BidStatus) => {
  const isCancelled =
    listing.auction3MP?.canceled || listing.auctionEnt?.canceled
  if (isCancelled) {
    return {
      type: TagType.error,
      messageId: 'Auction.tag.canceled',
    }
  }

  const isEnded =
    (listing.auction3MP?.closed || listing.auctionEnt?.closed) && !isCancelled
  if (isEnded) {
    return {
      type: TagType.normal,
      messageId: 'Auction.tag.ended',
    }
  }

  const hasUserBid =
    bidStatus === BidStatus.WINNING || bidStatus === BidStatus.LOSING
  const isLive =
    !listing.auction3MP?.closed && !listing.auctionEnt?.closed && !isCancelled
  if (hasUserBid && isLive) {
    if (bidStatus === BidStatus.WINNING) {
      return {
        type: TagType.success,
        messageId: 'Auction.tag.winning',
      }
    } else {
      return {
        type: TagType.error,
        messageId: 'Auction.tag.losing',
      }
    }
  }

  return null
}

const SUMMARIZE_LABEL_KEY: Record<ManifestSummarizeLabelVariant, string> = {
  summarize: 'SearchResultCard.summarize',
  'ai-summary': 'SearchResultCard.aiSummary',
  'quick-view': 'SearchResultCard.quickView',
}

const SUMMARIZE_BUTTON_NAME: Record<ManifestSummarizeLabelVariant, string> = {
  summarize: 'manifest_summarize',
  'ai-summary': 'manifest_ai_summary',
  'quick-view': 'manifest_quick_view',
}

const Card = ({
  listing,
  listingDetailsUrl,
  bidStatus,
  children,
  screenName,
  position,
  isNewSellerListing,
  onSummarizeClick,
  summarizeLabelVariant = 'summarize',
}: {
  listing: SearchResult
  listingDetailsUrl: string
  bidStatus: BidStatus
  children: React.ReactNode
  screenName: ScreenName
  position: number
  isNewSellerListing: boolean
  onSummarizeClick?: () => void
  summarizeLabelVariant?: ManifestSummarizeLabelVariant
}) => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const { itemListId, itemListName } = useItemListContext()
  const is3mp = listing.siteAbb.toLowerCase() === 'tmp'

  // Null-safe read: only the ALP grid mounts CompareSelectionProvider. On every
  // other surface (carousels) `compare` is null → compare mode off, no overlay.
  const compare = useOptionalCompareSelection()
  const isCompareMode = compare?.compareMode ?? false
  const isCompareSelected =
    compare?.selectedIds.includes(listing.listingId) ?? false
  const isAtCompareLimit = compare?.atLimit ?? false

  // Drives both what renders and what the click reports, so the two cannot
  // diverge.
  const { shouldShowDealBadge, segmentProps: dealBadgeSegmentProps } =
    useDealBadgeData(listing, screenName)
  const showsNote = useShowsCardNote(listing.watchlistStatus)

  const handleSummarizeClick = () => {
    trackButtonClicked({
      buttonName: SUMMARIZE_BUTTON_NAME[summarizeLabelVariant],
      listing_id: listing.listingId,
    })
    onSummarizeClick?.()
  }

  const handleClickListingCard = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // In compare mode the card body toggles selection instead of navigating.
    // (This also drives the at-limit toast via toggleListing.)
    if (isCompareMode) {
      e.preventDefault()
      e.stopPropagation()
      compare?.toggleListing(listing.listingId)
      return
    }

    const ctaUrl = listingDetailsUrl.startsWith('http')
      ? new URL(listingDetailsUrl).pathname
      : listingDetailsUrl

    trackButtonClicked({
      buttonName: 'view_details',
      cta_url: ctaUrl,
      listing_source: is3mp ? '3MP' : 'magento',
      referrer: document.referrer,
      seller_name_temp: is3mp ? '3MP' : listing.storefrontName,
      url: document.URL,
      listing_position: position,
      id: listing.id,
      recommended_rank: listing.customRank,
      is_control: listing.randomSetLabel,
      is_bstock_blue: isBstockBlue(listing),
      is_new_seller: isNewSellerListing,
      user_bid_status: bidStatus,
      // The bidder-count pair arrives through ListingCard's analytics context.
      ...dealBadgeSegmentProps,
    })

    const item = transformListingToGTMItem(
      searchResultToGTMItemInput(listing),
      position,
      itemListId,
      itemListName
    )
    sendGTMEvent({ ecommerce: null })
    sendGTMEvent({
      event: 'select_item',
      platform: GTM_PLATFORM,
      is_new_seller: isNewSellerListing,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [item],
      },
    })
  }

  const dateExpires = getWatchlistDateExpires(new Date(listing.endTime))
  const watchlistGtmItem = useMemo(
    () =>
      transformListingToGTMItem(searchResultToGTMItemInput(listing), position),
    [listing, position]
  )
  const isBStockBlueListing = isBstockBlue(listing)
  const isPromotedListing = !!listing.isPromoted

  // Border precedence, preserved from the original interpolation: new-seller
  // wins over promoted, which wins over the default border. Background is driven
  // separately via data-bstock-blue / data-promoted (see CardLink).
  const cardBorderVariant = isNewSellerListing
    ? 'new-seller'
    : isPromotedListing
      ? 'promoted'
      : 'default'

  const statusTag = getStatusTag(listing, bidStatus)
  const shouldOpenInNewTab =
    screenName === ScreenName.AllAuction || screenName === ScreenName.Homepage

  // Support localhost: convert absolute dev 3MP links to relative
  const ldpHref =
    is3mp && listingDetailsUrl.startsWith('https://bstock-dev.com')
      ? (() => {
          const { pathname, search, hash } = new URL(listingDetailsUrl)
          return `${pathname}${search}${hash}`
        })()
      : listingDetailsUrl

  return (
    <Wrapper>
      <CardLink
        href={ldpHref}
        onClick={handleClickListingCard}
        aria-label={listing.title}
        data-testid="search-result-card"
        target={shouldOpenInNewTab ? '_blank' : undefined}
        rel={shouldOpenInNewTab ? 'noopener' : undefined}
        data-bstock-blue={isBStockBlueListing}
        data-promoted={isPromotedListing}
        data-variant={cardBorderVariant}
        data-compare-selected={isCompareSelected}
        data-has-summarize-button={!!(listing.manifest && onSummarizeClick)}
        // We can use next's links for magento listings since there's not a
        // next page on the other side, but we want fast-path navigation for
        // 3MP listings.
        as={is3mp ? Link : 'a'}
        prefetch={is3mp ? false : undefined}
      >
        <ImageSection>
          {statusTag && (
            <BidStatusTag
              type={statusTag.type}
              data-testid="bid-status-tag"
              $isCompareMode={isCompareMode}
            >
              <FormattedMessage id={statusTag.messageId} />
            </BidStatusTag>
          )}
          <Image
            src={
              listing.primaryImageUrl ||
              '/home-portal/images/fallbackListingImage.png'
            }
            alt={formatMessage(
              { id: 'Auction.image.alt' },
              { title: listing.title }
            )}
            width={236}
            height={168}
          />
        </ImageSection>
        {children}
      </CardLink>
      {isCompareMode && (
        <CompareCardOverlay
          listingId={listing.listingId}
          listingTitle={listing.title}
          isSelected={isCompareSelected}
          isAtLimit={isAtCompareLimit}
          onToggle={() => compare?.toggleListing(listing.listingId)}
        />
      )}
      {listing.manifest && onSummarizeClick && (
        <SummarizeButtonContainer>
          <SummarizeButton
            type="button"
            onClick={handleSummarizeClick}
            data-testid="summarize-button"
          >
            <FontAwesomeIcon icon={faClipboardList} size="sm" />
            <FormattedMessage id={SUMMARIZE_LABEL_KEY[summarizeLabelVariant]} />
          </SummarizeButton>
        </SummarizeButtonContainer>
      )}
      <TopRightContainer>
        <CtaColumn>
          {listing.watchlistStatus && (
            <WatchlistCTA
              dateExpires={dateExpires}
              listingId={listing.listingId}
              listingTitle={listing.title}
              isWatchlisted={listing.watchlistStatus.isWatchlisted}
              gtmItem={watchlistGtmItem}
              listingEndTime={listing.endTime}
              storefrontName={listing.storefrontName}
            />
          )}
          {showsNote && (
            <WatchlistNotePopover
              listingId={listing.listingId}
              listingTitle={listing.title}
              note={listing.watchlistStatus?.note}
              dateExpires={dateExpires}
              listingEndTime={listing.endTime}
              wasWatchlisted={!!listing.watchlistStatus?.isWatchlisted}
            />
          )}
        </CtaColumn>
        {(isBStockBlueListing || shouldShowDealBadge || isPromotedListing) && (
          <BadgesStack>
            {isPromotedListing && (
              <PromotedTag type={TagType.success} data-testid="promoted-tag">
                <FormattedMessage id="Auction.tag.promoted" />
              </PromotedTag>
            )}
            {isBStockBlueListing && (
              <BStockBlueTag
                type={TagType.auction}
                data-testid="bStock-blue-tag"
              >
                <FormattedMessage id="Auction.tag.bStockBlue" />
              </BStockBlueTag>
            )}
            {shouldShowDealBadge && (
              <FmvDealBadge variant="card" data-testid="alp-deal-badge" />
            )}
          </BadgesStack>
        )}
      </TopRightContainer>
    </Wrapper>
  )
}

export default Card
